import {
  useGetCurrentWorkspace,
  useUpdateWorkspace,
} from '@/service/workspace/hook';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import DeleteWorkspace from './DeleteWorkspace';
import { WORKSPACE_STATUS } from '@/service/workspace/constants';
import { getWorkspaces } from '@/service/workspace';
import { toast } from 'sonner';

const EditWorkspace: React.FC = () => {
  const { data } = useGetCurrentWorkspace();
  const { mutateAsync, isPending } = useUpdateWorkspace();

  const isDefaultWorkspace =
    data?.workspace_status === WORKSPACE_STATUS.DEFAULT;
  const formSchema = z.object({
    workspaceName: z.string().min(2).max(50),
    workspaceId: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (data) {
      form.setValue('workspaceName', data.workspace_name!);
      form.setValue('workspaceId', data.workspace_id!);
    }
  }, [data]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const workspaces = await getWorkspaces();
    if (
      workspaces.filter(
        (workspace) => workspace.workspace_name === data.workspaceName
      ).length > 0
    ) {
      form.setError('workspaceName', {
        type: 'manual',
        message: 'Workspace name already exists',
      });
      return;
    }
    await mutateAsync({
      id: data.workspaceId,
      data: {
        workspace_name: data.workspaceName,
      },
    });
    toast.message('Workspace updated');
  };
  if (!data) return <div></div>;

  return (
    <>
      <div className="container flex flex-col gap-3">
        <p className="text-xl font-semibold">Workspace Setting</p>
        <div>
          <div className="mb-6 border-t" />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex max-w-md flex-col gap-6">
                <FormField
                  control={form.control}
                  name="workspaceName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Workspace Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="workspaceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Workspace ID</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="self-start"
                  disabled={isPending}
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </div>
        <div className="my-6 border-t" />
        {!isDefaultWorkspace && (
          <DeleteWorkspace workspaceId={data?.workspace_id || ''} />
        )}
      </div>
    </>
  );
};
export default EditWorkspace;
